import styled, { css } from "styled-components";
import { Link } from "../Router/Link";
import { SIDEBAR } from "./constants";
import { animationDuration, breakpoints } from "./theme";

export const SIDEBAR_PADDING_VERTICAL = SIDEBAR.basePadding * 2 - 6;
export const SIDEBAR_PADDING_HORIZONTAL = SIDEBAR.basePadding * 2 - 4;

export const focusVisible = (
  x = "2px",
  y = x,
  position = "relative",
  left = x,
  right = x
) => css`
  position: ${position};

  :focus-visible::after {
    content: "";
    display: block;
    position: absolute;
    box-sizing: border-box;
    top: ${y};
    bottom: ${y};
    left: ${left};
    right: ${right};
    border-radius: 4px;
    pointer-events: none;
    border: 2px solid var(--sinch-sys-color-border-focus);
  }
`;

const buttonStyle = (isActive: boolean) => css`
  all: initial;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  cursor: pointer;
  flex: 1;
  color: ${isActive
    ? "var(--sinch-comp-color-menu-option-color-default-border-selected)"
    : "var(--sinch-comp-color-menu-option-color-default-border-active)"};

  :hover {
    color: var(--sinch-comp-color-menu-option-color-default-border-active);
  }

  ${focusVisible("0px")}
`;
type ButtonProps = {
  $isActive: boolean;
};
export const Button = styled(Link)<ButtonProps>`
  ${({ $isActive }) => buttonStyle($isActive)}
`;

export const ButtonExternal = styled.a<ButtonProps>`
  ${({ $isActive }) => buttonStyle($isActive)}
`;

export const IconButton = styled.button`
  all: initial;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  cursor: pointer;
  flex: 0 1;
  padding: 8px 0 8px 10px;

  ${focusVisible("0px", "0px", "relative", "8px")}

  @media screen and (max-height: ${breakpoints.heightM}px) {
    & {
      padding: 4px 0 4px 10px;
    }
  }
`;

export const BackButton = styled(Link)`
  padding: 12px 16px;
  display: inline-flex;
  gap: 12px;
  color: var(--sinch-sys-color-primary-default);

  --sinch-global-color-icon: var(--sinch-sys-color-primary-default);

  flex: 1;
  text-decoration: none;
  cursor: pointer;
  outline: none;
  font: var(--sinch-sys-font-body-emphasize);

  :hover {
    background: var(--sinch-sys-color-container-main-hover);
  }

  ${focusVisible("0px")}
`;

export const ICON_WRAPPER_SIZE = 40;
export const ICON_WRAPPER_SMALL_SIZE = 32;
export const IconWrapper = styled.div`
  position: relative;
  width: ${ICON_WRAPPER_SIZE}px;
  height: ${ICON_WRAPPER_SIZE}px;
  box-sizing: border-box;
  border-radius: 4px;
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;

  svg {
    fill: #929292;
  }

  svg path {
    fill: #929292;
  }

  @media screen and (max-height: ${breakpoints.heightM}px) {
    & {
      width: ${ICON_WRAPPER_SMALL_SIZE}px;
      height: ${ICON_WRAPPER_SMALL_SIZE}px;
      border-radius: 4px;
    }

    svg {
      height: 18px;
      width: 18px;
    }
  }
`;

export const IconWrapperBadgeContainer = styled.div`
  position: absolute;
  top: -3px;
  right: -3px;
  display: flex;
`;

export const ItemText = styled.h1<{ noWrap?: boolean; isActive?: boolean }>`
  margin: 0 0 0 ${SIDEBAR.itemTextMargin}px;
  font: var(--sinch-sys-font-desktop-title-s);
  color: var(--sinch-comp-select-button-color-default-icon-initial);
  font-size: var(--sinch-ref-typography-font-size-14);
  line-height: var(--sinch-ref-typography-line-height-16);
  ${({ isActive }) =>
    !isActive &&
    css`
      font-weight: var(--sinch-ref-typography-font-weight-400);
      color: var(--sinch-ref-color-main-stormy-400);
    `};
  ${({ noWrap }) =>
    noWrap &&
    css`
      white-space: nowrap;
    `}
`;

type ItemButtonContainerProps = {
  isActive: boolean;
};
export const ItemButtonContainer = styled.div<ItemButtonContainerProps>`
  display: inline-flex;
  justify-content: space-between;
  flex: 1;
  border-radius: 8px;
  background-color: ${({ isActive }) =>
    isActive ? "var(--sinch-sys-color-container-main-active)" : "transparent"};

  :hover {
    background: var(--sinch-sys-color-container-main-hover);
  }

  @media screen and (max-height: ${breakpoints.heightM}px) {
    & {
      border-radius: 4px;
    }
  }
`;

type ItemContainerProps = {
  isFlex?: boolean;
  noPadding?: boolean;
  isCollapsed: boolean;
};
export const ItemContainer = styled.div<ItemContainerProps>`
  display: flex;
  ${({ isFlex }) =>
    isFlex &&
    css`
      flex: 1;
    `}
  justify-content: flex-start;
  ${({ isCollapsed }) =>
    isCollapsed &&
    css`
      align-items: center;
    `}
  flex-direction: column;

  ${({ noPadding, isCollapsed }) =>
    noPadding
      ? css`
          padding: 0;
        `
      : css`
          padding: 10px ${isCollapsed ? 0 : "10px"};

          @media screen and (max-height: ${breakpoints.heightS}px) {
            & {
              padding: 4px ${isCollapsed ? 0 : "10px"};
            }
          }
        `}
`;

export const SubMenuPanel = styled.nav`
  display: none;
  min-height: 100%;
  opacity: 0;

  &.enter {
    display: block;
    transition: opacity ${animationDuration.medium}ms linear
      ${animationDuration.medium}ms;
  }

  &.enter-active,
  &.enter-done {
    display: block;
    opacity: 1;
  }

  &.exit {
    transition: opacity ${animationDuration.medium}ms linear;
  }

  &.exit-active,
  &.exit-done {
    opacity: 0;
  }

  &.exit-done {
    display: none;
  }
`;

export const StyledSubMenuHeader = styled.header`
  color: var(--sinch-sys-color-text-default);
  font-size: 19px;
  font-weight: var(--sinch-ref-typography-font-weight-400);
  line-height: var(--sinch-ref-typography-line-height-22);
  padding: 32px 32px 21px;
`;
